/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Breadrumb from '@ba-ui-toolkit/breadcrumb';
import Toolbar from '@ba-ui-toolkit/toolbar';
import Button from '@ba-ui-toolkit/button';
import Link from '@ba-ui-toolkit/link';
import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import filter16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/filter_16';
import filterApplied16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/filter-applied_16';
import add16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/add_16';
import menuOverflow16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/overflow-menu--horizontal_16';
import arrowsVertical16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/arrows--vertical_16';
import search16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/search_16';
import configure16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/configure_16';
import stringResource from './nls/StringResource';

import DataView from './dataview/DataView';
import DataViewItem from './dataview/DataViewItem';
import ErrorBoundary from './errorboundary';
import navigatorStyle from './Navigator.scss';

function getString(value) {
	return stringResource.get(value);
}
const navigatorClassName = navigatorStyle.navigatorClassName.replace(/"/g, '');

const DATAVIEW_HEADER_HEIGHT = 48;
const { sanitize } = propsUtils;
const commonPorps = {
	variant: 'frameless',
	intent: '',
};

Toolbar.Add = class Add extends Component {
	render() {
		return <Button {...commonPorps} icon={add16.id} {...this.props} />;
	}
};
Toolbar.Filter = class Filter extends Component {
	static propTypes = {
		isFilterDirty: PropTypes.bool,
	};
	render() {
		const { isFilterDirty, ...otherProps } = this.props;
		const filterIcon = isFilterDirty ? filterApplied16.id : filter16.id;

		return <Button {...commonPorps} icon={filterIcon} {...otherProps} />;
	}
};
Toolbar.Sort = class Sort extends Component {
	render() {
		return <Button {...commonPorps} icon={arrowsVertical16.id} {...this.props} />;
	}
};
Toolbar.Configure = class Configure extends Component {
	render() {
		return <Button {...commonPorps} icon={configure16.id} {...this.props} />;
	}
};
Toolbar.Search = class Search extends Component {
	render() {
		return <Button {...commonPorps} icon={search16.id} {...this.props} />;
	}
};
Toolbar.Menu = class Menu extends Component {
	render() {
		return <Button {...commonPorps} icon={menuOverflow16.id} {...this.props} />;
	}
};
Toolbar.ToolbarLink = class ToolbarLink extends Component {
	render() {
		return <Link {...this.props} />;
	}
};

@ErrorBoundary()
export default class Navigator extends Component {
	static propTypes = {
		/** Array of files displayed in the navigator list */
		files: PropTypes.array.isRequired,
		/** The items in the navigator menu that are selected */
		selectedItems: PropTypes.oneOfType([PropTypes.object, PropTypes.number]).isRequired,
		/** Width of the navigator */
		width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		/** Height of the navigator */
		height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		/** Controls the spinner gif visibility */
		loaded: PropTypes.bool,
		/** Controls the visibility of the back button */
		hideBackButton: PropTypes.bool,
		/** Allows for grouping of list items */
		grouped: PropTypes.bool,
		/** Hides data view header on scroll */
		scrollToHideDataViewHeader: PropTypes.bool,
		/** Displays items in a tile view */
		tileView: PropTypes.bool,
		/** Ancestors of current data view */
		ancestors: PropTypes.array,
		/** List of items in the context menu */
		itemMenuContent: PropTypes.array,
		/** Child element inside the navigator */
		children: PropTypes.element,
		/** Extra functionality for file interaction */
		action: PropTypes.object,
		/** Specifies what is displayed when there is no content */
		emptyState: PropTypes.shape({
			icon: PropTypes.object,
			label: PropTypes.string,
		}),
		/** Callback for navigating through breadcrumb */
		onNav: PropTypes.func,
		/** Callback for navigating back through breadcrumb */
		onNavBack: PropTypes.func,
		/** Loads more data for infinite scroll */
		getMoreData: PropTypes.func,
		/** Handler for adding a folder */
		onAddFolder: PropTypes.func,
		/** Handler for creating a new file */
		onSaveNewFile: PropTypes.func,
		/** Handler for changing item selection */
		onSelectionChange: PropTypes.func,
		/** Handler when esc key is pressed during new file creation */
		dismissCreateFile: PropTypes.func,
		/** Callback for clicking a context menu item */
		onItemMenu: PropTypes.func,
		/** Renders the data view */
		dataViewRenderer: PropTypes.func.isRequired,
		/** Renders the header */
		dataViewHeaderRenderer: PropTypes.func,
		/** Renders the footer */
		dataViewFooterRenderer: PropTypes.func,
		/** Renders the toolbar */
		toolbarRenderer: PropTypes.func,
		/** Handler for changing active item */
		onActiveChange: PropTypes.func,
		/** Callback for clicking ... on a list item to open a context menu  */
		onMenuClick: PropTypes.func,
		/** Specifies how to group items */
		groupBy: PropTypes.func,
		/** Allow selection to be based on id rather than index */
		getId: PropTypes.func,
		/** Specifies how many items can be selected at a time */
		selectionMode: PropTypes.oneOf(['none', 'disabled', 'single', 'multi']),
		/** Custom class name */
		className: PropTypes.string,
		/** Custom styles */
		style: PropTypes.object,
		/** Size of the breadcrumb */
		breadcrumbSize: Breadrumb.propTypes.size,
		/** Extra data view options */
		dataViewExtraOptions: PropTypes.object,
		/** Flag to make selected items an object */
		toSelectionMap: PropTypes.bool,
		/** Direction of text */
		textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
		/** Place holder text for new items input box */
		newItemPlaceholder: PropTypes.string,
	};

	static defaultProps = {
		height: '100%',
		breadcrumbSize: 'small',
		onNav: () => {},
		onNavBack: () => {},
		className: '',
		styles: {},
		dataViewExtraOptions: {},
	};

	state = {
		folder: '',
		height: '100%',
		width: '100%',
		filter: false,
		checked: false,
	};

	componentWillReceiveProps(newProps) {
		const { ancestors } = this.props;
		if (!ancestors || !newProps.ancestors) return;
		if (ancestors.length !== newProps.ancestors.length) {
			this._revertSelection();
		}
	}

	_revertSelection = () => {
		this.props.onSelectionChange({});
	};

	_renderToolbar = () => this.props.toolbarRenderer && this.props.toolbarRenderer(Toolbar);

	_renderBreadcrumb = () => {
		const { hideBackButton, ancestors, textDir } = this.props;
		if (!ancestors) {
			return null;
		}

		return (
			<Breadrumb
				size="small"
				data-tid="ba-common-navigator__breadcrumb"
				hideBackButton={hideBackButton}
				textDir={textDir}
				ancestors={ancestors}
				historyCallback={this._onNavBack}
				navCallback={this._nav}
			/>
		);
	};

	_renderHeader = () => {
		if (!this._renderBreadcrumb() && !this._renderToolbar()) {
			return null;
		}

		return (
			<div className={`${navigatorClassName}__header-wrapper`}>
				<div className={`${navigatorClassName}__header`}>
					{this._renderBreadcrumb()}
					{this._renderToolbar()}
				</div>
			</div>
		);
	};

	_itemsRenderer = (items, ref) => {
		const { onSaveNewFile, dismissCreateFile, action, files, newItemPlaceholder } = this.props;

		return (
			<div ref={ref}>
				{action && action.type === 'CREATE' && (
					<DataViewItem length={files.length + 1} index={-1}>
						<DataViewItem.Input
							icon={action.icon}
							value={getString('defaultFolder')}
							onSaveNewFile={onSaveNewFile}
							dismissCreateFile={dismissCreateFile}
							fileType={action.value}
							placeholder={newItemPlaceholder}
						/>
					</DataViewItem>
				)}
				{items}
			</div>
		);
	};
	_;
	_renderDataView = () => {
		const {
			// bool
			tileView,
			loaded,
			grouped,
			scrollToHideDataViewHeader,
			toSelectionMap,
			// array
			files,
			itemMenuContent,
			// obj
			selectedItems,
			emptyState,
			dataViewExtraOptions,
			// string
			selectionMode,
			// cb
			getMoreData,
			onSelectionChange,
			onActiveChange,
			onItemMenu,
			onMenuClick,
			groupBy,
			// hook
			dataViewRenderer,
			getId,
		} = this.props;

		return (
			<DataView
				{...dataViewExtraOptions}
				// bool
				tileView={tileView}
				loaded={loaded}
				grouped={grouped}
				groupBy={groupBy}
				toSelectionMap={toSelectionMap}
				// array
				data={files}
				itemMenuContent={itemMenuContent}
				// obj
				selectedItems={selectedItems}
				emptyState={emptyState}
				// string
				selectionMode={selectionMode}
				// cb
				getMoreData={getMoreData}
				onSelectionChange={onSelectionChange}
				onActiveChange={onActiveChange}
				onItemMenu={onItemMenu}
				onMenuClick={onMenuClick}
				dataViewRenderer={dataViewRenderer}
				itemsRenderer={this._itemsRenderer}
				onWheel={scrollToHideDataViewHeader ? this._onWheel : undefined}
				scrollToHideDataViewHeader={scrollToHideDataViewHeader}
				getId={getId}
			/>
		);
	};
	_onDownWards = e => {
		const { scrollTop } = e.currentTarget;
		const { style } = this._dataviewHeader;
		style.marginTop = `${-Math.min(scrollTop, DATAVIEW_HEADER_HEIGHT)}px`;
	};
	_onUpWards = () => {
		const { style } = this._dataviewHeader;
		style.marginTop = '0px';
	};
	_onWheel = e => {
		if (e.deltaY > 0) {
			this._onDownWards(e);
		} else {
			this._onUpWards();
		}
	};
	_onNavBack = () => {
		const { onNavBack } = this.props;
		onNavBack();
	};

	_nav = index => {
		const { onNav } = this.props;
		onNav(index);
	};
	_renderDataViewFooter() {
		const { dataViewFooterRenderer } = this.props;
		if (dataViewFooterRenderer) {
			return (
				<div className={`${navigatorClassName}__dataview-footer`}>
					{dataViewFooterRenderer()}
				</div>
			);
		}

		return null;
	}
	_renderDataViewHeader() {
		const { dataViewHeaderRenderer, scrollToHideDataViewHeader } = this.props;
		const dataViewHeaderClassName = cx(`${navigatorClassName}__dataview-header`, {
			scrollable: scrollToHideDataViewHeader,
		});
		if (dataViewHeaderRenderer) {
			return (
				<div className={dataViewHeaderClassName} ref={this._dataviewHeaderRef}>
					{dataViewHeaderRenderer()}
				</div>
			);
		}

		return null;
	}
	_dataviewHeaderRef = ref => {
		this._dataviewHeader = ref;
	};
	render() {
		const {
			height,
			width,
			className,
			style,
			ancestors,
			toolbarRenderer,
			...others
		} = this.props;
		const styles = {
			width,
			height,
			...style,
		};
		const mainClassName = cx(`${navigatorClassName}__main`, {
			noHeader: (!ancestors || ancestors.length === 0) && !toolbarRenderer,
		});

		return (
			<div
				{...sanitize(others, Navigator)}
				className={`${navigatorClassName} ${className}`}
				style={styles}
			>
				{this._renderHeader()}
				<div className={mainClassName}>
					{this._renderDataViewHeader()}
					{this._renderDataView()}
					{this._renderDataViewFooter()}
				</div>
			</div>
		);
	}
}
